import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

interface OrganizationMembership {
  organization_id: string;
  role: string;
  analysis_count: number;
  joined_at: string;
}

export function useOrganization() {
  const { user } = useAuth();
  const [membership, setMembership] = useState<OrganizationMembership | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnboarding, setIsOnboarding] = useState(false);

  useEffect(() => {
    const fetchOrganizationMembership = async () => {
      if (!user) {
        setMembership(null);
        setIsOnboarding(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get user's profile first to know which org they should be in
        const { data: profileData } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        // Query for organization membership
        // If profile has org_id, prefer that one (handles duplicate memberships)
        const query = supabase
          .from('organization_members')
          .select('organization_id, role, analysis_count, joined_at')
          .eq('user_id', user.id);

        // If profile specifies an org, query for that specific one
        if (profileData?.organization_id) {
          query.eq('organization_id', profileData.organization_id);
        }

        const { data, error: fetchError } = await query.maybeSingle();

        if (fetchError) {
          // Only log unexpected errors (not "no rows" errors)
          if (fetchError.code !== 'PGRST116') {
            console.warn('Error fetching organization membership:', fetchError);
            setError(fetchError.message);
          }
          // User has no organization - needs onboarding
          setMembership(null);
          setIsOnboarding(true);
        } else if (data) {
          // User has organization membership
          setMembership(data);
          setIsOnboarding(false);
        } else {
          // No membership found - needs onboarding
          setMembership(null);
          setIsOnboarding(true);
        }
      } catch (err) {
        console.error('Unexpected error in organization membership check:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // On error, assume needs onboarding
        setMembership(null);
        setIsOnboarding(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationMembership();
  }, [user]);

  return {
    membership,
    organizationId: membership?.organization_id || null,
    userRole: membership?.role || null,
    isAdmin: membership?.role === 'admin',
    hasOrganization: !!membership,
    isOnboarding,
    loading,
    error,
  };
}
